import { useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { pregameGoingIds } from '@shared/pregames'
import { HeaderLink } from '../../components/HeaderLink'
import { DashedRow } from '../../components/LinkGroupList'
import { PregameCard } from '../../components/PregameCard'
import { PregameDialog } from '../../components/PregameDialog'
import { PregameRsvpRow } from '../../components/PregameRsvpRow'
import { Section } from '../../components/Section'
import { useUserProfilesWithLoading } from '../../hooks/useUserProfiles'
import { useGuestGuard } from '../../hooks/useGuestGuard'
import { setPregameRsvp, useSavedPlaces, type EventPregames } from '../../hooks/usePregames'
import { hostPregamePrompt } from '../../lib/guestPrompts'
import { isEventActive } from '../../lib/formatDate'
import { getNow } from '../../lib/clock'
import type { Event, Group } from '../../types'

/** The viewer's own pre-game (or a CTA to host one) plus the ones they're invited to. */
export function PregamesSection({ event, pregames, currentUserId, groups }: {
  event: Event
  pregames: EventPregames
  /** Signed-in, non-anonymous viewer; null for guests. */
  currentUserId: string | null
  groups: Group[]
}) {
  const { locale } = useLocaleContext()
  const { prompt } = useGuestGuard()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { mine, invited, loading } = pregames
  // Subscribed here, not in the dialog, so a new pre-game opens already prefilled.
  const savedPlaces = useSavedPlaces(currentUserId)
  const { profiles, loading: profilesLoading } = useUserProfilesWithLoading([
    ...(currentUserId ? [currentUserId] : []),
    ...[mine, ...invited].flatMap(p => (p ? pregameGoingIds(p) : [])),
  ])

  const canHost = !event.cancelled && isEventActive(event, getNow())
  if (!loading && !mine && invited.length === 0 && !canHost) return null

  const memberGroups = currentUserId ? groups.filter(g => g.memberIds.includes(currentUserId)) : []
  const openHost = () => (currentUserId ? setDialogOpen(true) : prompt(hostPregamePrompt()))
  // A viewer hosts at most one pre-game per event, so the CTA goes away once they have one;
  // while only invites are showing it shrinks to a header link instead of a full row.
  const hostAction = !loading && !mine && canHost && invited.length > 0 && (
    <HeaderLink onClick={openHost}>
      {fbt('Host one', 'Header link in the event page pre-games section that opens the form to host a pre-game')}
    </HeaderLink>
  )

  return (
    <Section
      title={fbt('Pre-games', 'Section heading on the event page for pre-games the viewer hosts or is invited to')}
      action={hostAction}
    >
      {loading ? (
        <PregameCard loading />
      ) : (
        <>
          {mine ? (
            <PregameCard
              pregame={mine}
              host={profiles[mine.hostId]}
              isMine
              locale={locale}
              onEdit={canHost ? () => setDialogOpen(true) : undefined}
              footer={<PregameRsvpRow pregame={mine} currentUserId={currentUserId} profiles={profiles} />}
            />
          ) : canHost && invited.length === 0 && (
            <DashedRow
              onClick={openHost}
              label={fbt('Host a pre-game', 'Dashed row on the event page that opens the form to host a pre-game')}
            />
          )}
          {invited.map(p => (
            <PregameCard
              key={p.id}
              pregame={p}
              host={profiles[p.hostId]}
              hostLoading={profilesLoading}
              locale={locale}
              footer={
                <PregameRsvpRow
                  pregame={p}
                  currentUserId={currentUserId}
                  profiles={profiles}
                  onRsvp={currentUserId && canHost ? status => setPregameRsvp(p, currentUserId, status).catch(() => {}) /* toasted */ : undefined}
                />
              }
            />
          ))}
        </>
      )}

      {currentUserId && (
        <PregameDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          event={event}
          userId={currentUserId}
          groups={memberGroups}
          pregame={mine}
          savedPlaces={savedPlaces}
          locale={locale}
        />
      )}
    </Section>
  )
}
